// src/hooks/useOnDutyContact.js - Tenant-aware version
import { useQuery } from 'react-query';
import { fetchUtils } from 'react-admin';
import { createTenantQueryKey, extractTenantFromHostname } from '../utils/tenant';

// Helper function for HTTP requests with tenant awareness
const httpClient = (url, options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  const token = localStorage.getItem('token');
  options.headers.set('Authorization', `Bearer ${token}`);
  
  // Get API URL with proper tenant
  const tenant = extractTenantFromHostname();
  
  // Determine base API URL (read from runtime config)
  const nodeEnv = window.APP_CONFIG?.NODE_ENV || 'production';

  let apiUrl;
  if (nodeEnv === 'development') {
    apiUrl = `http://${tenant}.api.wygc.eu:8080/api`;
  } else {
    apiUrl = `https://${tenant}.api.wygc.eu/api`;
  }
  
  // Ensure the URL is properly formed
  const fullUrl = url.startsWith('http') ? url : `${apiUrl}/${url.replace(/^\//, '')}`;
  
  console.log(`On-duty contact query to: ${fullUrl}`);
  
  return fetchUtils.fetchJson(fullUrl, options);
};

export const useOnDutyContact = (teamId, options = {}) => {
  const tenant = extractTenantFromHostname();
  
  return useQuery(
    createTenantQueryKey(['onDutyContact', teamId, tenant]),
    async () => {
      if (!teamId) {
        throw new Error('Team ID not available');
      }

      // First, get querytoken from settings
      const settingsUrl = `teams/${teamId}/settings`;
      const { json: settings } = await httpClient(settingsUrl);
      const queryTokenSetting = settings.find(setting => setting.key === 'querytoken');

      if (!queryTokenSetting || !queryTokenSetting.value) {
        throw new Error('Query token not found in settings');
      }

      const queryToken = queryTokenSetting.value;

      // Now fetch on-duty contact using the token
      const onDutyUrl = `contacts/onduty?querytoken=${queryToken}`;
      const { json } = await httpClient(onDutyUrl);
      
      return json;
    },
    {
      enabled: !!teamId,
      staleTime: 5 * 60 * 1000, // 5 minutes cache
      cacheTime: 10 * 60 * 1000, // 10 minutes cache
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: false,      // Don't refetch when component mounts
      retry: (failureCount, error) => {
        // Don't retry if we get a 404 (no contact on duty) as that's an expected state
        if (error?.status === 404) return false;
        return failureCount < 2; // Otherwise retry up to 2 times
      },
      ...options
    }
  );
};